import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

class PDFService {
  // ✅ Formatage des montants — sans Intl.NumberFormat (cause des "/" dans jsPDF)
  // Intl.NumberFormat('fr-FR') utilise un espace insécable \u202F comme séparateur
  // de milliers, que jsPDF ne sait pas rendre et affiche comme "/"
  formatMontant(montant) {
    const n = parseFloat(montant) || 0;
    const fixed = n.toFixed(2);
    const [intPart, decPart] = fixed.split('.');
    // Séparateur de milliers = espace simple, compatible jsPDF
    const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return `${intFormatted},${decPart} HTG`;
  }

  // Formatage de la date
  formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  }

  // Générer PDF pour un achat spécifique
  async generateAchatPDF(achat, fournisseurs, produits) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // En-tête
    doc.setFontSize(20);
    doc.setTextColor(33, 150, 243);
    doc.text('BON D\'ACHAT', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`N°: ${achat.numero || achat.id}`, 14, 35);
    doc.text(`Date: ${this.formatDate(achat.date_achat)}`, 14, 42);
    
    // Informations fournisseur
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('FOURNISSEUR:', 14, 55);
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(achat.fournisseur_detail?.nom || 'N/A', 14, 62);
    if (achat.fournisseur_detail?.telephone) {
      doc.text(`Tél: ${achat.fournisseur_detail.telephone}`, 14, 69);
    }
    
    // Informations succursale
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text('SUCCURSALE:', pageWidth - 80, 55);
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(achat.succursale_nom || 'N/A', pageWidth - 80, 62);
    
    // Ligne de séparation
    doc.setDrawColor(200, 200, 200);
    doc.line(14, 80, pageWidth - 14, 80);
    
    // Tableau des produits
    const tableColumn = [
      'Produit',
      'Conditionnement',
      'Qté',
      'Qté/Unité',
      'Total Unités',
      'Prix Achat',
      'Total'
    ];
    
    const tableRows = [];
    
    if (achat.lignes && achat.lignes.length > 0) {
      achat.lignes.forEach(ligne => {
        const quantiteTotale = ligne.quantite_totale || 
          (parseFloat(ligne.quantite) * parseFloat(ligne.quantite_par_conditionnement));
        
        tableRows.push([
          ligne.produit_detail?.nom || 'N/A',
          ligne.conditionnement || 'N/A',
          parseFloat(ligne.quantite).toString(),
          ligne.quantite_par_conditionnement.toString(),
          quantiteTotale.toString(),
          this.formatMontant(ligne.prix_achat_unitaire),
          this.formatMontant(ligne.total || 
            (quantiteTotale * parseFloat(ligne.prix_achat_unitaire)))
        ]);
      });
    }
    
    // Ajouter le tableau
    autoTable(doc, {
      startY: 90,
      head: [tableColumn],
      body: tableRows,
      theme: 'striped',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 50 },
        5: { halign: 'right' },
        6: { halign: 'right' }
      },
      foot: [['', '', '', '', '', 'TOTAL:', this.formatMontant(achat.montant_total)]],
      footStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'right'
      }
    });
    
    // Notes si présentes
    if (achat.notes) {
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text('Notes:', 14, finalY);
      doc.setTextColor(0, 0, 0);
      doc.text(achat.notes, 14, finalY + 7);
    }
    
    // Pied de page
    const today = new Date().toLocaleDateString('fr-FR');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Document généré le ${today}`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
    
    // Télécharger le PDF
    doc.save(`achat_${achat.numero || achat.id}_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  // Générer rapport PDF avec filtres
  async generateRapportPDF(achats, filtres, statistiques) {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Titre
    doc.setFontSize(22);
    doc.setTextColor(33, 150, 243);
    doc.text('RAPPORT DES ACHATS', pageWidth / 2, 20, { align: 'center' });
    
    // Période
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    let periodeText = 'Période: ';
    if (filtres.dateDebut && filtres.dateFin) {
      periodeText += `du ${this.formatDate(filtres.dateDebut)} au ${this.formatDate(filtres.dateFin)}`;
    } else if (filtres.dateDebut) {
      periodeText += `à partir du ${this.formatDate(filtres.dateDebut)}`;
    } else if (filtres.dateFin) {
      periodeText += `jusqu'au ${this.formatDate(filtres.dateFin)}`;
    } else {
      periodeText += 'Toutes les dates';
    }
    doc.text(periodeText, 14, 35);
    
    // Filtres supplémentaires
    let yPos = 42;
    if (filtres.fournisseurNom) {
      doc.text(`Fournisseur: ${filtres.fournisseurNom}`, 14, yPos);
      yPos += 7;
    }
    if (filtres.succursaleNom) {
      doc.text(`Succursale: ${filtres.succursaleNom}`, 14, yPos);
      yPos += 7;
    }
    if (filtres.searchTerm) {
      doc.text(`Recherche: "${filtres.searchTerm}"`, 14, yPos);
    }
    
    // Cartes de statistiques
    const statsY = 60;
    
    // Fond gris pour les stats
    doc.setFillColor(245, 245, 245);
    doc.rect(14, statsY, pageWidth - 28, 35, 'F');
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('STATISTIQUES', 20, statsY + 7);
    
    doc.setFontSize(10);
    doc.text(`Nombre d'achats: ${statistiques.totalAchats}`, 20, statsY + 17);
    doc.text(`Montant total: ${this.formatMontant(statistiques.montantTotal)}`, 20, statsY + 24);
    doc.text(`Moyenne par achat: ${this.formatMontant(statistiques.moyenneAchat)}`, 20, statsY + 31);
    
    if (statistiques.totalProduits) {
      doc.text(`Total produits: ${statistiques.totalProduits}`, pageWidth / 2 + 20, statsY + 17);
    }
    if (statistiques.nbFournisseurs) {
      doc.text(`Fournisseurs: ${statistiques.nbFournisseurs}`, pageWidth / 2 + 20, statsY + 24);
    }
    
    // Tableau des achats
    const tableColumn = [
      'N°',
      'Date',
      'Fournisseur',
      'Succursale',
      'Nb Produits',
      'Montant'
    ];
    
    const tableRows = achats.map(achat => [
      achat.numero || achat.id,
      this.formatDate(achat.date_achat),
      achat.fournisseur_detail?.nom || 'N/A',
      achat.succursale_nom || 'N/A',
      (achat.lignes?.length || 0).toString(),
      this.formatMontant(achat.montant_total)
    ]);
    
    autoTable(doc, {
      startY: 105,
      head: [tableColumn],
      body: tableRows,
      theme: 'striped',
      headStyles: {
        fillColor: [33, 150, 243],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        5: { halign: 'right' }
      }
    });
    
    // Pied de page
    const today = new Date().toLocaleDateString('fr-FR');
    const time = new Date().toLocaleTimeString('fr-FR');
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Rapport généré le ${today} à ${time}`, pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
    
    // Nom du fichier
    let fileName = 'rapport_achats';
    if (filtres.dateDebut || filtres.dateFin) {
      fileName += `_${filtres.dateDebut || 'debut'}_${filtres.dateFin || 'fin'}`;
    }
    if (filtres.fournisseurNom) {
      fileName += `_${filtres.fournisseurNom.replace(/\s+/g, '_')}`;
    }
    if (filtres.succursaleNom) {
      fileName += `_${filtres.succursaleNom.replace(/\s+/g, '_')}`;
    }
    fileName += `_${new Date().toISOString().split('T')[0]}.pdf`;
    
    doc.save(fileName);
  }
}

export default new PDFService();